import { zodResolver } from '@hookform/resolvers/zod';
import { SubmitHandler, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import * as z from 'zod';

import { Mail, Check } from 'lucide-react';
import React, { useState, useEffect } from 'react';

import { useNavigate, useLocation } from 'react-router-dom';

import { useAppDispatch } from '@/hooks/useAppDispatch';

import { authService } from '@/services/auth.service';

import Logo from '@/assets/images/logo512.png';
import { setUser } from '@/store/slices/auth';
import { store } from '@/store';

import Checkbox from '../atoms/Checkbox';
import CountdownTimer from '../atoms/CountDownTimer';
import InputField from '../atoms/InputField';
import PasswordField from '../atoms/PasswordField';
import { Button } from '../ui/button';

import { useSignUp } from '@clerk/clerk-react';
import { apiService } from '@/api/apiConfig';
import { AuthApiService } from '@/services/authApi.service';

// password schema based on backend regex rules
const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(20, 'Password must be at most 20 characters')
  .refine((value) => /[A-Z]/.test(value), {
    message: 'Password must contain at least one uppercase letter'
  })
  .refine((value) => /[a-z]/.test(value), {
    message: 'Password must contain at least one lowercase letter'
  })
  .refine((value) => /\d/.test(value), {
    message: 'Password must contain at least one number'
  })
  .refine((value) => /[!@#$%^&*]/.test(value), {
    message: 'Password must contain at least one special character (!@#$%^&*)'
  });

// form schema
const schema = z
  .object({
    email: z.string().min(2, 'Email is required').email('Invalid email address'),
    password: passwordSchema,
    confirm_password: z.string(),
    agreeTerms: z.boolean().refine((val) => val === true, {
      message: 'You must agree to the terms'
    })
  })
  .refine((data) => data.password === data.confirm_password, {
    message: 'Must be same with password',
    path: ['confirm_password']
  });

type FormData = z.infer<typeof schema>;

const SignUpForm: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [isSignupSuccess, setIsSignupSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [isSyncingBackend, setIsSyncingBackend] = useState(false);
  const [isTimerActive, setIsTimerActive] = useState(false);

  const { signUp, isLoaded: signUpLoaded, setActive } = useSignUp();

  const navigate = useNavigate();
  const location = useLocation();

  const dispatch = useAppDispatch();

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors, isValid, isDirty }
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: {
      email: '',
      password: '',
      confirm_password: '',
      agreeTerms: false
    }
  });

  // Watch password field for validation rules
  const password = watch('password');

  // Password validation rules
  const passwordValidations = {
    hasMinLength: password?.length >= 8,
    hasUppercase: /[A-Z]/.test(password || ''),
    hasLowercase: /[a-z]/.test(password || ''),
    hasNumber: /\d/.test(password || ''),
    hasSpecialChar: /[!@#$%^&*]/.test(password || '')
  };

  const allPasswordRulesMet =
    passwordValidations.hasMinLength &&
    passwordValidations.hasUppercase &&
    passwordValidations.hasLowercase &&
    passwordValidations.hasNumber &&
    passwordValidations.hasSpecialChar;

  // Load form data from navigation state if available
  useEffect(() => {
    const stateData = location.state as Partial<FormData>;
    if (stateData) {
      if (stateData.email) {
        setValue('email', stateData.email, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
      }
      if (stateData.password) {
        setValue('password', stateData.password, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true
        });
      }
      if (stateData.confirm_password) {
        setValue('confirm_password', stateData.confirm_password, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true
        });
      }
      if (stateData.agreeTerms !== undefined) {
        setValue('agreeTerms', stateData.agreeTerms, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true
        });
      }
    }
  }, [location.state, setValue]);

  const onSubmit: SubmitHandler<FormData> = async (data: FormData) => {
    if (!signUpLoaded || !signUp) {
      toast.error('Authentication not ready');
      return;
    }

    setLoading(true);
    try {
      console.log('Starting Clerk signup...', data.email);

      // Step 1: Create user with Clerk (this will trigger email verification)
      const signUpAttempt = await signUp.create({
        emailAddress: data.email,
        password: data.password
      });

      console.log('Clerk signup status:', signUpAttempt.status);

      // Step 2: Handle verification requirement
      if (signUpAttempt.status === 'missing_requirements') {
        // Prepare email verification (this sends the verification email)
        await signUpAttempt.prepareEmailAddressVerification({
          strategy: 'email_code'
        });

        // Show success message and verification instructions
        setUserEmail(data.email);
        setIsSignupSuccess(true);
        setIsTimerActive(true); // Start cooldown timer
        toast.success('Verification email sent! Please check your inbox.');

        // Note: User will need to click the link in their email
        // After verification, they should be redirected back to your app
        // You'll need to handle the callback in a separate route
        return;
      }

      // Unexpected status
      throw new Error(`Unexpected signup status: ${signUpAttempt.status}`);
    } catch (err: any) {
      console.error('Signup error:', err);

      // Handle specific Clerk errors
      if (err.errors) {
        const clerkErrors = err.errors;
        clerkErrors.forEach((error: any) => {
          if (error.code === 'form_identifier_exists') {
            toast.error('This email is already registered. Please sign in instead.');
          } else if (error.message) {
            toast.error(error.message);
          }
        });
      } else {
        toast.error(err.message || 'Signup failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignInClick = () => {
    navigate('/login');
  };

  const handleTermsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const currentValues = getValues();
    navigate('/terms', {
      state: {
        email: currentValues.email,
        password: currentValues.password,
        confirm_password: currentValues.confirm_password,
        agreeTerms: currentValues.agreeTerms
      }
    });
  };

  const handlePrivacyPolicyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const currentValues = getValues();
    navigate('/privacy-policy', {
      state: {
        email: currentValues.email,
        password: currentValues.password,
        confirm_password: currentValues.confirm_password,
        agreeTerms: currentValues.agreeTerms
      }
    });
  };

  // helper function to sync user with backend with retry logic
  const syncUserWithBackend = async (
    clerkUserId: string,
    maxRetries = 3
  ): Promise<{ success: boolean; error?: any }> => {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        console.log(`Attempting backend sync (attempt ${attempt + 1}/${maxRetries})...`);

        await apiService.request({
          method: 'POST',
          url: '/auth/sync-user',
          data: {
            clerkUserId
          }
        });

        console.log('Backend sync successful');
        return { success: true };
      } catch (error) {
        console.error(`Backend sync attempt ${attempt + 1} failed:`, error);

        // If this was the last attempt, return the error
        if (attempt === maxRetries - 1) {
          return { success: false, error };
        }

        // Exponential backoff: 1s, 2s, 4s
        const delayMs = 1000 * Math.pow(2, attempt);
        console.log(`Retrying in ${delayMs}ms...`);
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }

    return { success: false };
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!signUp || !verificationCode) {
      toast.error('Please enter the verification code');
      return;
    }

    setVerifyingCode(true);
    try {
      // step 1: Attempt to verify the email with the code
      const completeSignUp = await signUp.attemptEmailAddressVerification({
        code: verificationCode
      });

      console.log('Verification status:', completeSignUp.status);

      if (completeSignUp.status === 'complete') {
        if (!completeSignUp.createdUserId) {
          console.error('No user ID after verification');
          toast.error('Verification completed but user ID is missing. Please try again.');
          return;
        }

        // step 2: Sync user with backend (with retry logic)
        setIsSyncingBackend(true);
        const syncResult = await syncUserWithBackend(completeSignUp.createdUserId);
        setIsSyncingBackend(false);

        if (syncResult.success) {
          // step 3: Set the active session
          await setActive({ session: completeSignUp.createdSessionId, redirectUrl: '/onboarding/profile' });

          // Set session persistence flag for new users (default to persistent)
          localStorage.setItem('clerkAuthSession', 'true');
          sessionStorage.removeItem('clerkAuthSession');

          toast.success('Account created successfully! Redirecting to onboarding...');
        } else {
          console.error('Backend sync failed after all retries:', syncResult.error);
          toast.error('Unable to complete account setup. Please try again or contact support.');
        }
      } else {
        toast.error('Verification incomplete. Please try again.');
      }
    } catch (err: any) {
      console.error('Verification error:', err);

      if (err.errors) {
        const clerkErrors = err.errors;
        clerkErrors.forEach((error: any) => {
          if (error.code === 'form_code_incorrect') {
            toast.error('Incorrect verification code. Please try again.');
          } else if (error.message) {
            toast.error(error.message);
          }
        });
      } else {
        toast.error(err.message || 'Verification failed. Please try again.');
      }
    } finally {
      setVerifyingCode(false);
    }
  };

  return (
    <div className="max-w-[450px] mx-auto flex flex-col justify-center py-10 px-5 md:px-0 h-full laptop-h:h-fit">
      <img className="max-w-[130px] mx-auto md:hidden" src={Logo} alt="batjet-logo" />
      <h1 className="mt-7 mb-2 text-xl text-center font-bold uppercase md:text-left md:text-[28px]">
        {isSignupSuccess ? (
          <div className="flex items-center gap-2">
            <Mail className="w-10 h-10 text-primary" />
            <span>Verify Your Email</span>
          </div>
        ) : (
          'SignUp'
        )}
      </h1>
      <p className="hidden md:block text-lg text-secondary leading-[22.5px]">
        {isSignupSuccess ? '' : 'Sign up and take the first step towards achieving your goals with BATJET.'}
      </p>
      {!isSignupSuccess ? (
        <form className="mt-7 flex flex-col gap-5" onSubmit={handleSubmit(onSubmit)} autoComplete="off">
          <InputField
            label={'Email'}
            placeholder="Input here"
            name="email"
            type="email"
            register={register}
            error={errors.email}
            autoComplete="username"
          />
          <div>
            <PasswordField
              label={'Password'}
              placeholder="Enter password..."
              name="password"
              register={register}
              error={errors.password}
              autoComplete="new-password"
            />
            <div className="mt-3 space-y-2">
              <div
                className="flex items-center gap-2 text-sm transition-opacity duration-200"
                style={{ opacity: passwordValidations.hasMinLength ? 1 : 0.6 }}
              >
                <Check className="w-4 h-4 text-gray-500" />
                <span>Minimum 8 characters</span>
              </div>
              <div
                className="flex items-center gap-2 text-sm transition-opacity duration-200"
                style={{ opacity: passwordValidations.hasUppercase ? 1 : 0.6 }}
              >
                <Check className="w-4 h-4 text-gray-500" />
                <span>At least one uppercase letter (A-Z)</span>
              </div>
              <div
                className="flex items-center gap-2 text-sm transition-opacity duration-200"
                style={{ opacity: passwordValidations.hasLowercase ? 1 : 0.6 }}
              >
                <Check className="w-4 h-4 text-gray-500" />
                <span>At least one lowercase letter (a-z)</span>
              </div>
              <div
                className="flex items-center gap-2 text-sm transition-opacity duration-200"
                style={{ opacity: passwordValidations.hasNumber ? 1 : 0.6 }}
              >
                <Check className="w-4 h-4 text-gray-500" />
                <span>At least one number (0-9)</span>
              </div>
              <div
                className="flex items-center gap-2 text-sm transition-opacity duration-200"
                style={{ opacity: passwordValidations.hasSpecialChar ? 1 : 0.6 }}
              >
                <Check className="w-4 h-4 text-gray-500" />
                <span>At least one special character (!@#$%^&*)</span>
              </div>
            </div>
          </div>
          <PasswordField
            label={'Confirm Password'}
            placeholder="Enter password..."
            name="confirm_password"
            register={register}
            error={errors.confirm_password}
            autoComplete="new-password"
          />
          <Checkbox
            name="agreeTerms"
            register={register}
            error={errors.agreeTerms}
            label={
              <p className="text-xs md:text-sm text-secondary leading-[0.9125rem]">
                I agree with BATJET{' '}
                <span onClick={handleTermsClick} className="text-primary cursor-pointer hover:underline">
                  Terms of Service
                </span>{' '}
                and acknowledge the{' '}
                <span onClick={handlePrivacyPolicyClick} className="text-primary cursor-pointer hover:underline">
                  Privacy Policy
                </span>
                .
              </p>
            }
          />

          <Button
            disabled={!isValid || !isDirty || loading}
            className="mt-2 w-full rounded-[24px] text-base font-semibold h-[44px] md:h-[64px] md:text-[18px]"
          >
            {loading ? '  SIGNING UP...' : '  SIGN UP'}
          </Button>
          <p className="text-sm md:text-lg text-center">
            Already have an account?{' '}
            <button onClick={handleSignInClick} className="text-primary font-bold">
              Sign In
            </button>
          </p>
        </form>
      ) : (
        <div className="mt-10">
          <form onSubmit={handleVerifyCode} className="flex flex-col gap-5">
            <div className="mb-4">
              <p className="text-sm md:text-base text-center md:text-left text-gray-700 mb-2">
                We've sent a verification code to <strong>{userEmail}</strong>
              </p>
              <p className="text-xs md:text-sm text-center md:text-left text-gray-500">
                Please check your email and enter the 6-digit code below
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <label className="inline-block text-[#495057] font-medium text-lg mb-2">Verification Code</label>
              <input
                type="text"
                placeholder="Enter 6-digit code"
                value={verificationCode}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                  setVerificationCode(value);
                }}
                maxLength={6}
                className="w-full text-center text-2xl tracking-widest bg-[#F8F9FA] rounded-[20px] border border-[#DEE2E6] font-medium p-4 outline-none focus:border-[#ADB5BD]"
              />
              <p className="text-xs text-gray-500 text-center">Enter the 6-digit code from your email</p>
            </div>

            <Button
              type="submit"
              disabled={verificationCode.length !== 6 || verifyingCode}
              className="w-full rounded-[24px] text-base font-semibold h-[44px] md:h-[64px] md:text-[18px]"
            >
              {isSyncingBackend ? 'SETTING UP YOUR ACCOUNT...' : verifyingCode ? 'VERIFYING...' : 'VERIFY EMAIL'}
            </Button>

            <div className="flex flex-col gap-3">
              <Button
                type="button"
                onClick={() => {
                  setIsSignupSuccess(false);
                  setUserEmail('');
                  setVerificationCode('');
                  setIsTimerActive(false); // Reset timer
                }}
                variant="outline"
                className="w-full rounded-[24px] text-base font-semibold h-[44px] md:h-[64px] md:text-[18px]"
              >
                Back to Sign Up
              </Button>

              <p className="text-sm md:text-lg text-center">
                {isTimerActive ? 'Please wait before able to resend code' : "Didn't receive the code?"}{' '}
                {isTimerActive ? (
                  <span className="text-primary font-semibold">
                    <CountdownTimer
                      duration={60}
                      isActive={isTimerActive}
                      onActiveChange={setIsTimerActive}
                      onComplete={() => {
                        console.log('Timer completed');
                      }}
                    />
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={async () => {
                      if (!signUp) return;
                      try {
                        await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
                        setIsTimerActive(true); // Start cooldown timer
                        toast.success('Verification code resent!');
                      } catch (error) {
                        toast.error('Failed to resend code. Please try again.');
                      }
                    }}
                    className="text-primary font-bold"
                  >
                    Resend Code
                  </button>
                )}
              </p>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default SignUpForm;
